import { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';

// Tolerant numeric parse: accepts formatted strings ("$1,234.50", "1 234,50")
// and keeps legitimate zeros (parseFloat(value) || null used to turn 0 into null).
const parseNumericValue = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  let s = String(value).trim();
  if (s === '') return null;
  s = s.replace(/[^0-9.,-]/g, '');                                    // drop $, spaces, letters
  if (s.indexOf(',') > -1 && s.indexOf('.') === -1) s = s.replace(',', '.'); // "1234,50" -> "1234.50"
  s = s.replace(/,/g, '');                                             // thousands separators
  if (s === '' || s === '-' || s === '.') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

/**
 * Custom hook for Excel file upload functionality
 * Extracts common state and logic from upload modals
 * 
 * @param {Object} config - Upload configuration
 * @param {Object} config.columnMapping - Excel column name to DB column mapping
 * @param {Array} config.numericColumns - Array of DB column names that should be parsed as numbers
 * @param {Array} config.dateColumns - Array of DB column names that should be parsed as dates
 * @param {Array} config.integerColumns - Array of DB column names that should be parsed as integers (default: ['id'])
 */
export const useExcelUpload = (config = {}) => {
  const {
    columnMapping = {},
    numericColumns = [],
    dateColumns = [],
    integerColumns = ['id'],
  } = config;

  // State
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [step, setStep] = useState('select'); // 'select' | 'preview' | 'uploading'

  const fileInputRef = useRef(null);

  // Reset all state
  const resetModal = useCallback(() => {
    setSelectedFile(null);
    setParsedData(null);
    setValidationError('');
    setStep('select');
    setUploadProgress(0);
    setUploadStatus('');
    setLoading(false);
    setDragActive(false);
  }, []);

  // Drag event handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e, onFileSelect) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  // Parse Excel file using the column mapping
  const parseExcelFile = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array', cellDates: true });
          
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // Case-insensitive header lookup ("PRICE" matches a "Price" mapping).
          const mappingByLower = {};
          Object.entries(columnMapping).forEach(([excelCol, dbCol]) => {
            mappingByLower[String(excelCol).trim().toLowerCase()] = dbCol;
          });

          // Headers are not always on row 1 (e.g. returned purchase-requisition
          // files have a title + header block first). Scan the first 25 rows and
          // pick the row with the most recognized column headers as the header row.
          const asRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', cellDates: true });
          let headerRowIdx = 0, bestMatches = 0;
          for (let i = 0; i < Math.min(asRows.length, 25); i++) {
            const matches = (asRows[i] || []).filter(
              (cell) => mappingByLower[String(cell).trim().toLowerCase()]
            ).length;
            if (matches > bestMatches) { bestMatches = matches; headerRowIdx = i; }
          }
          if (bestMatches === 0) {
            reject(new Error('No recognized column headers found in the file. Expected one of: ' + Object.keys(columnMapping).slice(0, 8).join(', ') + '…'));
            return;
          }
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            defval: '', range: headerRowIdx, cellDates: true,
          });

          if (jsonData.length === 0) {
            reject(new Error('The Excel file is empty or has no data rows'));
            return;
          }

          const mappedData = jsonData.map((row) => {
            const mappedRow = {};
            
            Object.entries(row).forEach(([excelCol, value]) => {
              const dbCol = mappingByLower[String(excelCol).trim().toLowerCase()];
              if (dbCol) {
                // Handle integer columns (like id)
                if (integerColumns.includes(dbCol)) {
                  mappedRow[dbCol] = value === '' ? null : parseInt(value) || null;
                }
                // Handle numeric columns (floats/percentages)
                else if (numericColumns.includes(dbCol)) {
                  mappedRow[dbCol] = parseNumericValue(value);
                }
                // Handle date columns
                else if (dateColumns.includes(dbCol)) {
                  if (value instanceof Date) {
                    mappedRow[dbCol] = value.toISOString().split('T')[0];
                  } else {
                    mappedRow[dbCol] = value === '' ? null : value;
                  }
                }
                // Default to string
                else {
                  mappedRow[dbCol] = value === '' ? null : String(value);
                }
              }
            });
            
            return mappedRow;
          });

          // Filter out empty rows
          const filteredData = mappedData.filter(row => 
            Object.values(row).some(val => val !== null && val !== '')
          );

          resolve(filteredData);
        } catch (error) {
          reject(new Error('Failed to parse Excel file: ' + error.message));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  }, [columnMapping, numericColumns, dateColumns, integerColumns]);

  // Handle file selection
  const handleFileSelect = useCallback(async (file) => {
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setValidationError('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    setSelectedFile(file);
    setValidationError('');
    setLoading(true);
    setUploadStatus('Parsing Excel file...');

    try {
      const data = await parseExcelFile(file);
      setParsedData(data);
      setStep('preview');
      setUploadStatus('');
    } catch (error) {
      setValidationError(error.message);
      setSelectedFile(null);
    } finally {
      setLoading(false);
    }
  }, [parseExcelFile]);

  // Handle file input change
  const handleFileInputChange = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  }, [handleFileSelect]);

  // Set uploading state
  const startUpload = useCallback(() => {
    setStep('uploading');
    setLoading(true);
    setUploadProgress(0);
    setUploadStatus('Uploading data...');
  }, []);

  // Handle upload success
  const handleUploadSuccess = useCallback((message = 'Upload complete!') => {
    setUploadProgress(100);
    setUploadStatus(message);
  }, []);

  // Handle upload error
  const handleUploadError = useCallback((error) => {
    setValidationError(error);
    setStep('preview');
    setLoading(false);
  }, []);

  return {
    // State
    state: {
      dragActive,
      selectedFile,
      loading,
      uploadProgress,
      uploadStatus,
      parsedData,
      validationError,
      step,
    },
    
    // Refs
    fileInputRef,
    
    // Handlers
    handleDrag,
    handleDrop,
    handleFileSelect,
    handleFileInputChange,
    parseExcelFile,
    resetModal,
    startUpload,
    handleUploadSuccess,
    handleUploadError,
    
    // State setters for custom handling
    setValidationError,
    setLoading,
    setUploadProgress,
    setUploadStatus,
    setStep,
    setParsedData,
  };
};

export default useExcelUpload;

